import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import PublicIcon from "@mui/icons-material/Public";
import RepeatIcon from "@mui/icons-material/Repeat";
import SyncIcon from "@mui/icons-material/Sync";
import type { EventDisplayInfo, EventHoveringInfo, MountInfo } from "@fullcalendar/react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import type { SvgIconComponent } from "@mui/icons-material";
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  calendarEventColors,
  calendarEventKindLabels,
  isCalendarEventPreview,
  type CalendarEventPreview,
} from "calendar/calendar-event";
import {
  handleCalendarEventDidMount,
  handleCalendarEventWillUnmount,
} from "calendar/_views/calendar-event-presentation";

/** Long enough that sweeping the pointer across the month does not flash a card on every event. */
export const calendarEventPreviewOpenDelayMs = 300;
/** Short enough to feel immediate, long enough that moving onto the next event keeps the card up. */
export const calendarEventPreviewCloseDelayMs = 100;

const PreviewPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.tooltip,
  // The card only describes the event; the click that opens its details belongs to the event underneath.
  pointerEvents: "none",
}));
const PreviewCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "accentColor",
})<{ accentColor: string }>(({ accentColor, theme }) => ({
  width: 320,
  maxWidth: `calc(100vw - ${theme.spacing(2)})`,
  borderLeft: `4px solid ${accentColor}`,
  padding: theme.spacing(1.5, 2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.25),
}));
const KindLabel = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
}));
const KindDot = styled("span", {
  shouldForwardProp: (prop) => prop !== "dotColor",
})<{ dotColor: string }>(({ dotColor }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: dotColor,
  flexShrink: 0,
}));
const KindText = styled(Typography)({ lineHeight: 1.6 });
// A div, not subtitle1's default h6: the card is transient and should not add to the page's outline.
const PreviewTitle = styled("div")(({ theme }) => ({
  ...theme.typography.subtitle1,
  fontWeight: 600,
  lineHeight: 1.3,
  overflowWrap: "anywhere",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
}));
const DetailRows = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const DetailRowLayout = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "20px 1fr",
  columnGap: theme.spacing(1.25),
  alignItems: "start",
  minWidth: 0,
}));
const DetailIcon = styled("span")(({ theme }) => ({
  display: "flex",
  color: theme.palette.action.active,
  paddingTop: 1,
  "& svg": { fontSize: 18 },
}));
const DetailText = styled(Box)({ minWidth: 0 });
const DescriptionText = styled(Typography)({
  whiteSpace: "pre-line",
  overflowWrap: "anywhere",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 4,
  overflow: "hidden",
});
const ActionHint = styled(Typography)({ textAlign: "right" });

export function CalendarEventPreviewCard({ preview }: Readonly<{ preview: CalendarEventPreview }>) {
  const accentColor = calendarEventColors[preview.kind];
  const AccessIcon = preview.access?.reason === "sourceManaged" ? SyncIcon : LockOutlinedIcon;
  return (
    <PreviewCard elevation={8} accentColor={accentColor}>
      <Box>
        <KindLabel>
          <KindDot aria-hidden="true" dotColor={accentColor} />
          <KindText variant="overline" color="text.secondary">
            {calendarEventKindLabels[preview.kind]}
          </KindText>
        </KindLabel>
        <PreviewTitle>{preview.title}</PreviewTitle>
      </Box>
      <DetailRows>
        <DetailRow icon={AccessTimeIcon} label="When">
          <Typography variant="body2">{preview.date}</Typography>
          {preview.time && <Typography variant="body2" color="text.secondary">{preview.time}</Typography>}
        </DetailRow>
        {preview.repeats && (
          <DetailRow icon={RepeatIcon} label="Repeats">
            <Typography variant="body2">{preview.repeats}</Typography>
          </DetailRow>
        )}
        {preview.timeZoneId && (
          <DetailRow icon={PublicIcon} label="Time zone">
            <Typography variant="body2">Scheduled in {preview.timeZoneId.replaceAll("_", " ")}</Typography>
            <Typography variant="caption" color="text.secondary">Times are shown in your time zone</Typography>
          </DetailRow>
        )}
        {preview.description && (
          <DetailRow icon={NotesIcon} label="Description">
            <DescriptionText variant="body2" color="text.secondary">{preview.description}</DescriptionText>
          </DetailRow>
        )}
        {preview.access && (
          <DetailRow icon={AccessIcon} label="Access">
            <Typography variant="body2" color="text.secondary">{preview.access.note}</Typography>
          </DetailRow>
        )}
      </DetailRows>
      <Divider />
      <ActionHint variant="caption" color="text.secondary">{preview.actionHint}</ActionHint>
    </PreviewCard>
  );
}

function DetailRow({ icon: Icon, label, children }: Readonly<{ icon: SvgIconComponent; label: string; children: ReactNode }>) {
  return (
    <DetailRowLayout>
      <DetailIcon>
        <Icon titleAccess={label} />
      </DetailIcon>
      <DetailText>{children}</DetailText>
    </DetailRowLayout>
  );
}

type ShownPreview = {
  open: boolean;
  anchor: HTMLElement;
  preview: CalendarEventPreview;
};

/**
 * Preview state for a FullCalendar view's event cards. Spread `fullCalendarProps` onto the
 * FullCalendar and render {@link CalendarEventPreviewPopper} beside it; the props carry the shared
 * presentation hooks as well, so a view needs nothing else.
 *
 * A pointer waits {@link calendarEventPreviewOpenDelayMs} for the first card; once one is up, moving
 * onto another event swaps it straight away, the way a menu bar behaves. Keyboard focus opens the
 * card at once, blur closes it, and Escape dismisses it however it was opened.
 */
export function useCalendarEventPreview() {
  const [shown, setShown] = useState<ShownPreview | null>(null);
  const previewId = useId();
  const timer = useRef<number | undefined>(undefined);
  const pendingAnchor = useRef<HTMLElement | null>(null);
  const openAnchor = useRef<HTMLElement | null>(null);
  const focusCleanups = useRef(new WeakMap<HTMLElement, () => void>());

  const clearTimer = useCallback(() => {
    pendingAnchor.current = null;
    if (timer.current === undefined) return;
    window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);
  const close = useCallback(() => {
    clearTimer();
    openAnchor.current?.removeAttribute("aria-describedby");
    openAnchor.current = null;
    setShown((current) => current && { ...current, open: false });
  }, [clearTimer]);
  const reveal = useCallback((el: HTMLElement, preview: CalendarEventPreview) => {
    clearTimer();
    openAnchor.current?.removeAttribute("aria-describedby");
    openAnchor.current = el;
    el.setAttribute("aria-describedby", previewId);
    setShown({ open: true, anchor: el, preview });
  }, [clearTimer, previewId]);
  const eventMouseEnter = useCallback(({ el, event }: EventHoveringInfo) => {
    const preview: unknown = event.extendedProps.calendarEventPreview;
    if (!isCalendarEventPreview(preview)) return;
    if (openAnchor.current) {
      reveal(el, preview);
      return;
    }
    clearTimer();
    pendingAnchor.current = el;
    timer.current = window.setTimeout(() => reveal(el, preview), calendarEventPreviewOpenDelayMs);
  }, [clearTimer, reveal]);
  const eventMouseLeave = useCallback(() => {
    clearTimer();
    if (openAnchor.current) timer.current = window.setTimeout(close, calendarEventPreviewCloseDelayMs);
  }, [clearTimer, close]);
  const eventDidMount = useCallback((info: MountInfo<EventDisplayInfo>) => {
    handleCalendarEventDidMount(info);
    const { el, event } = info;
    const onFocus = () => {
      const preview: unknown = event.extendedProps.calendarEventPreview;
      // A click focuses the event too, and opens its details; only keyboard focus needs the card.
      if (isCalendarEventPreview(preview) && isKeyboardFocused(el)) reveal(el, preview);
    };
    const onBlur = () => {
      if (openAnchor.current === el) close();
    };
    focusCleanups.current.get(el)?.();
    el.addEventListener("focus", onFocus);
    el.addEventListener("blur", onBlur);
    focusCleanups.current.set(el, () => {
      el.removeEventListener("focus", onFocus);
      el.removeEventListener("blur", onBlur);
    });
  }, [close, reveal]);
  /** FullCalendar re-renders events on every refetch; a card must never open on, or stay on, a detached element. */
  const eventWillUnmount = useCallback((info: MountInfo<EventDisplayInfo>) => {
    handleCalendarEventWillUnmount(info);
    const { el } = info;
    focusCleanups.current.get(el)?.();
    focusCleanups.current.delete(el);
    if (openAnchor.current === el || pendingAnchor.current === el) close();
  }, [close]);
  const isOpen = shown?.open === true;
  useEffect(() => {
    if (!isOpen) return;
    // Captured and stopped so the first Escape closes only the card, not the day view's dialog around it.
    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== "Escape") return;
      keyEvent.stopPropagation();
      close();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [close, isOpen]);
  useEffect(() => clearTimer, [clearTimer]);

  return {
    shown,
    previewId,
    close,
    fullCalendarProps: { eventDidMount, eventWillUnmount, eventMouseEnter, eventMouseLeave },
  };
}

function isKeyboardFocused(element: HTMLElement): boolean {
  try {
    return element.matches(":focus-visible");
  } catch {
    // An engine without :focus-visible cannot tell a click from a Tab; describing the event is the safer mistake.
    return true;
  }
}

type CalendarEventPreviewPopperProps = {
  shown: ShownPreview | null;
  previewId: string;
};

export function CalendarEventPreviewPopper({ shown, previewId }: Readonly<CalendarEventPreviewPopperProps>) {
  if (!shown) return null;
  return (
    // Popper carries role="tooltip" itself, so the id the hovered event points at goes on it too.
    <PreviewPopper
      id={previewId}
      open={shown.open}
      anchorEl={shown.anchor}
      placement="bottom-start"
      transition
      modifiers={[
        { name: "offset", options: { offset: [0, 6] } },
        { name: "preventOverflow", options: { padding: 8 } },
      ]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={120}>
          <div>
            <CalendarEventPreviewCard preview={shown.preview} />
          </div>
        </Fade>
      )}
    </PreviewPopper>
  );
}
